import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| TYPES ||============================== //

// Provider-agnostic KB types (supports Confluence, Notion, etc.)
export type KBProviderType = 'confluence' | 'notion';

// Generic KB container (space in Confluence, workspace in Notion)
export interface KBContainer {
  id: string;
  key?: string;
  name: string;
  type: string;
}

// Generic KB page
export interface KBPage {
  id: string;
  title: string;
  url?: string;
  parentId?: string;
  space?: string;
  isEmbedded?: boolean;
}

// Embedded page info
export interface EmbeddedPage {
  title: string;
  url: string;
  embeddedAt: string;
}

// Documentation source attached to repository
export interface RepositoryDocumentationSource {
  id: string;
  repositoryId: string;
  sourceType: KBProviderType;
  sourceName: string;
  sourceUrl: string | null;
  totalPagesEmbedded: number;
  createdAt: string;
  updatedAt: string;
  pages?: EmbeddedPage[];
}

// Cost estimate response
export interface CostEstimate {
  totalPages: number;
  estimatedTokens: number;
  estimatedACU: number;
  estimatedCostUSD: number;
}

// Confluence installation info
export interface ConfluenceInstallation {
  id: string;
  cloudId: string;
  siteName: string;
  siteUrl: string;
  installedAt: string;
}

// Notion installation info
export interface NotionInstallationInfo {
  id: string;
  workspaceId: string;
  workspaceName: string;
  installedAt: string;
}

// ==============================|| INITIAL STATE ||============================== //

interface RepositoryKnowledgeBaseState {
  // Sources attached to repository
  sources: RepositoryDocumentationSource[];
  sourcesStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Confluence installations for the repository's organization
  confluenceInstallations: ConfluenceInstallation[];
  confluenceInstallationsStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Notion installations for the repository's organization
  notionInstallations: NotionInstallationInfo[];
  notionInstallationsStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Browsing state (containers = spaces in Confluence, workspaces in Notion)
  containers: KBContainer[];
  containersStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Pages within a container
  pages: KBPage[];
  pagesStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Cost estimation
  costEstimate: CostEstimate | null;
  costEstimateStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Embedding status
  embeddingStatus: 'idle' | 'loading' | 'success' | 'failed';

  // Error state
  error: string;
}

const initialState: RepositoryKnowledgeBaseState = {
  sources: [],
  sourcesStatus: 'idle',
  confluenceInstallations: [],
  confluenceInstallationsStatus: 'idle',
  notionInstallations: [],
  notionInstallationsStatus: 'idle',
  containers: [],
  containersStatus: 'idle',
  pages: [],
  pagesStatus: 'idle',
  costEstimate: null,
  costEstimateStatus: 'idle',
  embeddingStatus: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

/**
 * Get Confluence installations for a repository's organization
 * Uses the existing providers/installations endpoint with confluence filter
 */
export const getRepositoryConfluenceInstallations = createAsyncThunk(
  'repositoryKnowledgeBase/getConfluenceInstallations',
  async (repositoryId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/repositories/${repositoryId}/providers/installations?providerType=confluence`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Get Notion installations for a repository's organization
 * Uses the existing providers/installations endpoint with notion filter
 */
export const getRepositoryNotionInstallations = createAsyncThunk(
  'repositoryKnowledgeBase/getNotionInstallations',
  async (repositoryId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/repositories/${repositoryId}/providers/installations?providerType=notion`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Get all KB sources attached to a repository
 */
export const getRepositoryKBSources = createAsyncThunk(
  'repositoryKnowledgeBase/getSources',
  async (repositoryId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/knowledge-base/repositories/${repositoryId}/sources`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Browse containers (spaces/workspaces) for a provider
 * Installation is auto-detected on the backend
 */
export const browseRepositoryKBContainers = createAsyncThunk(
  'repositoryKnowledgeBase/browseContainers',
  async (
    { repositoryId, providerType }: { repositoryId: string; providerType: KBProviderType },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(
        `/knowledge-base/repositories/${repositoryId}/providers/${providerType}/containers`
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Browse pages within a container
 * Installation is auto-detected on the backend
 */
export const browseRepositoryKBPages = createAsyncThunk(
  'repositoryKnowledgeBase/browsePages',
  async (
    {
      repositoryId,
      providerType,
      containerId,
    }: {
      repositoryId: string;
      providerType: KBProviderType;
      containerId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (containerId) params.append('containerId', containerId);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await apiRequest(
        `/knowledge-base/repositories/${repositoryId}/providers/${providerType}/pages${queryString}`
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Estimate cost for embedding pages
 * Installation is auto-detected on the backend
 */
export const estimateRepositoryKBCost = createAsyncThunk(
  'repositoryKnowledgeBase/estimateCost',
  async (
    {
      repositoryId,
      providerType,
      pageIds,
      spaceName,
    }: {
      repositoryId: string;
      providerType: KBProviderType;
      pageIds: string[];
      spaceName: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/knowledge-base/repositories/${repositoryId}/providers/${providerType}/estimate-cost`, {
        method: 'POST',
        body: JSON.stringify({ pageIds, spaceName }),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Embed pages into repository KB
 * Installation is auto-detected on the backend
 */
export const embedRepositoryKBPages = createAsyncThunk(
  'repositoryKnowledgeBase/embedPages',
  async (
    {
      repositoryId,
      providerType,
      pageIds,
      spaceName,
    }: {
      repositoryId: string;
      providerType: KBProviderType;
      pageIds: string[];
      spaceName: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(`/knowledge-base/repositories/${repositoryId}/providers/${providerType}/embed`, {
        method: 'POST',
        body: JSON.stringify({ pageIds, spaceName }),
      });
      return response;
    } catch (error: any) {
      // Check for insufficient credits error - preserve this specific message
      if (error?.statusCode === 402 || error?.message?.includes('Insufficient credits')) {
        return rejectWithValue(error.message || 'Insufficient credits. Please add credits to continue.');
      }
      // Use generic error message for security
      return rejectWithValue('Something went wrong while embedding pages. Please try again.');
    }
  }
);

/**
 * Remove a KB source from repository
 */
export const removeRepositoryKBSource = createAsyncThunk(
  'repositoryKnowledgeBase/removeSource',
  async ({ repositoryId, sourceId }: { repositoryId: string; sourceId: string }, { rejectWithValue }) => {
    try {
      await apiRequest(`/knowledge-base/repositories/${repositoryId}/sources/${sourceId}`, {
        method: 'DELETE',
      });
      return sourceId;
    } catch (error: any) {
      // Use generic error message for security
      return rejectWithValue('Something went wrong while removing the knowledge base source. Please try again.');
    }
  }
);

/**
 * Remove a single page from a KB source
 */
export const removeRepositoryKBPage = createAsyncThunk(
  'repositoryKnowledgeBase/removePage',
  async (
    { repositoryId, sourceId, documentId }: { repositoryId: string; sourceId: string; documentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiRequest(
        `/knowledge-base/repositories/${repositoryId}/sources/${sourceId}/pages/${documentId}`,
        { method: 'DELETE' }
      );
      return { sourceId, documentId, ...(response?.response || response) };
    } catch (error: any) {
      // Use generic error message for security
      return rejectWithValue('Something went wrong while removing the page. Please try again.');
    }
  }
);

// ==============================|| SLICE ||============================== //

const slice = createSlice({
  name: 'repositoryKnowledgeBase',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = '';
    },
    clearContainers: (state) => {
      state.containers = [];
      state.containersStatus = 'idle';
    },
    clearPages: (state) => {
      state.pages = [];
      state.pagesStatus = 'idle';
    },
    clearCostEstimate: (state) => {
      state.costEstimate = null;
      state.costEstimateStatus = 'idle';
    },
    clearRepositoryKBState: () => initialState,
  },
  extraReducers: (builder) => {
    // Get Confluence installations
    builder
      .addCase(getRepositoryConfluenceInstallations.pending, (state) => {
        state.confluenceInstallationsStatus = 'loading';
      })
      .addCase(getRepositoryConfluenceInstallations.fulfilled, (state, action) => {
        const installations = action.payload?.response || action.payload || [];
        state.confluenceInstallations = Array.isArray(installations) ? installations : [];
        state.confluenceInstallationsStatus = 'success';
        state.error = '';
      })
      .addCase(getRepositoryConfluenceInstallations.rejected, (state, action) => {
        state.confluenceInstallationsStatus = 'failed';
        state.confluenceInstallations = [];
        state.error = (action.payload as string) || 'Failed to fetch Confluence installations';
      });

    // Get Notion installations
    builder
      .addCase(getRepositoryNotionInstallations.pending, (state) => {
        state.notionInstallationsStatus = 'loading';
      })
      .addCase(getRepositoryNotionInstallations.fulfilled, (state, action) => {
        const installations = action.payload?.response || action.payload || [];
        state.notionInstallations = Array.isArray(installations) ? installations : [];
        state.notionInstallationsStatus = 'success';
        state.error = '';
      })
      .addCase(getRepositoryNotionInstallations.rejected, (state, action) => {
        state.notionInstallationsStatus = 'failed';
        state.notionInstallations = [];
        state.error = (action.payload as string) || 'Failed to fetch Notion installations';
      });

    // Get sources
    builder
      .addCase(getRepositoryKBSources.pending, (state) => {
        state.sourcesStatus = 'loading';
      })
      .addCase(getRepositoryKBSources.fulfilled, (state, action) => {
        // API returns { projectSources, directSources, totalSources }
        // We only store directSources here (project sources are fetched via knowledge-base slice)
        const payload = action.payload?.response || action.payload || {};
        const directSources = payload.directSources || payload || [];
        state.sources = Array.isArray(directSources) ? directSources : [];
        state.sourcesStatus = 'success';
        state.error = '';
      })
      .addCase(getRepositoryKBSources.rejected, (state, action) => {
        state.sourcesStatus = 'failed';
        state.error = (action.payload as string) || 'Failed to fetch repository KB sources';
      });

    // Browse containers
    builder
      .addCase(browseRepositoryKBContainers.pending, (state) => {
        state.containersStatus = 'loading';
      })
      .addCase(browseRepositoryKBContainers.fulfilled, (state, action) => {
        const containers = action.payload?.response || action.payload || [];
        state.containers = Array.isArray(containers) ? containers : [];
        state.containersStatus = 'success';
        state.error = '';
      })
      .addCase(browseRepositoryKBContainers.rejected, (state, action) => {
        state.containersStatus = 'failed';
        state.error = (action.payload as string) || 'Failed to browse containers';
      });

    // Browse pages
    builder
      .addCase(browseRepositoryKBPages.pending, (state) => {
        state.pagesStatus = 'loading';
      })
      .addCase(browseRepositoryKBPages.fulfilled, (state, action) => {
        const pages = action.payload?.response || action.payload || [];
        state.pages = Array.isArray(pages) ? pages : [];
        state.pagesStatus = 'success';
        state.error = '';
      })
      .addCase(browseRepositoryKBPages.rejected, (state, action) => {
        state.pagesStatus = 'failed';
        state.error = (action.payload as string) || 'Failed to browse pages';
      });

    // Estimate cost
    builder
      .addCase(estimateRepositoryKBCost.pending, (state) => {
        state.costEstimateStatus = 'loading';
      })
      .addCase(estimateRepositoryKBCost.fulfilled, (state, action) => {
        state.costEstimate = action.payload?.response || action.payload;
        state.costEstimateStatus = 'success';
        state.error = '';
      })
      .addCase(estimateRepositoryKBCost.rejected, (state, action) => {
        state.costEstimateStatus = 'failed';
        state.error = (action.payload as string) || 'Failed to estimate cost';
      });

    // Embed pages
    builder
      .addCase(embedRepositoryKBPages.pending, (state) => {
        state.embeddingStatus = 'loading';
      })
      .addCase(embedRepositoryKBPages.fulfilled, (state) => {
        state.embeddingStatus = 'success';
        state.error = '';
        // Clear browsing state after successful embedding
        state.pages = [];
        state.costEstimate = null;
      })
      .addCase(embedRepositoryKBPages.rejected, (state, action) => {
        state.embeddingStatus = 'failed';
        state.error = (action.payload as string) || 'Failed to embed pages';
      });

    // Remove source
    builder
      .addCase(removeRepositoryKBSource.pending, (state) => {
        state.sourcesStatus = 'loading';
      })
      .addCase(removeRepositoryKBSource.fulfilled, (state, action) => {
        state.sources = state.sources.filter((s) => s.id !== action.payload);
        state.sourcesStatus = 'success';
        state.error = '';
      })
      .addCase(removeRepositoryKBSource.rejected, (state, action) => {
        state.sourcesStatus = 'failed';
        state.error = (action.payload as string) || 'Failed to remove KB source';
      });

    // Remove single page
    builder
      .addCase(removeRepositoryKBPage.pending, (state) => {
        // Don't set loading to avoid UI flicker
      })
      .addCase(removeRepositoryKBPage.fulfilled, (state, action) => {
        const { sourceId, documentId, sourceDeleted, remainingPages } = action.payload;
        if (sourceDeleted) {
          // If source was deleted (last page), remove it from sources
          state.sources = state.sources.filter((s) => s.id !== sourceId);
        } else {
          // Remove the page from the source's pages array
          const source = state.sources.find((s) => s.id === sourceId);
          if (source && source.pages) {
            source.pages = source.pages.filter((p: any) => p.documentId !== documentId);
            source.totalPagesEmbedded = remainingPages;
          }
        }
        state.error = '';
      })
      .addCase(removeRepositoryKBPage.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to remove page';
      });
  },
});

// ==============================|| ACTIONS & REDUCER ||============================== //

export const { clearError, clearContainers, clearPages, clearCostEstimate, clearRepositoryKBState } = slice.actions;

export default slice.reducer;

// ==============================|| SELECTORS ||============================== //

export const selectRepositoryKBSources = (state: any) => state.repositoryKnowledgeBase.sources;
export const selectRepositoryKBSourcesStatus = (state: any) => state.repositoryKnowledgeBase.sourcesStatus;

export const selectRepositoryConfluenceInstallations = (state: any) => state.repositoryKnowledgeBase.confluenceInstallations;
export const selectRepositoryConfluenceInstallationsStatus = (state: any) => state.repositoryKnowledgeBase.confluenceInstallationsStatus;

export const selectRepositoryNotionInstallations = (state: any) => state.repositoryKnowledgeBase.notionInstallations;
export const selectRepositoryNotionInstallationsStatus = (state: any) => state.repositoryKnowledgeBase.notionInstallationsStatus;

export const selectRepositoryKBContainers = (state: any) => state.repositoryKnowledgeBase.containers;
export const selectRepositoryKBContainersStatus = (state: any) => state.repositoryKnowledgeBase.containersStatus;

export const selectRepositoryKBPages = (state: any) => state.repositoryKnowledgeBase.pages;
export const selectRepositoryKBPagesStatus = (state: any) => state.repositoryKnowledgeBase.pagesStatus;

export const selectRepositoryKBCostEstimate = (state: any) => state.repositoryKnowledgeBase.costEstimate;
export const selectRepositoryKBCostEstimateStatus = (state: any) => state.repositoryKnowledgeBase.costEstimateStatus;

export const selectRepositoryKBEmbeddingStatus = (state: any) => state.repositoryKnowledgeBase.embeddingStatus;

export const selectRepositoryKBError = (state: any) => state.repositoryKnowledgeBase.error;
